/**
 * Enum labels — the words a code is shown as, in English and Hindi (schema §11, doc 05-admin/03 §10).
 *
 * Every status chip, picker and booking-form field reads these rows. Until this screen a label
 * change was a migration; now content edits it, and the server lints and audits each write.
 *
 * ⛔ **No delete and no new vocabulary.** A row is switched off, never removed, so the audit trail
 * always resolves; and a vocabulary is added by the migration that adds the code reading it — a
 * label for a name nothing reads is copy nobody sees. The server refuses both; this screen does
 * not offer them.
 *
 * ⛔ **Every refusal is the server's, shown verbatim.** The content lint names the rule and quotes
 * the span, and only the server knows which row already holds a key.
 */
import { useState } from 'react';
import { Card, Icon, StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { DataTable } from '../components/data';
import { Consequences, ScopeAction, REFUSED } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useCreateEnumLabel,
  useEnumLabels,
  useUpdateEnumLabel,
  type EnumLabelRow,
} from '../lib/api/hooks/enumLabels';

const LOCALES = ['en', 'hi'] as const;

/** Codes with an English label for everyone but no Hindi one — the gap this screen most often fills. */
export function missingHindi(rows: readonly EnumLabelRow[]): string[] {
  const hindi = new Set(
    rows.filter((r) => r.locale === 'hi' && r.audience === 'all').map((r) => r.enum_value),
  );
  return rows
    .filter((r) => r.locale === 'en' && r.audience === 'all' && !hindi.has(r.enum_value))
    .map((r) => r.enum_value);
}

function Editor({ row, onDone }: { row: EnumLabelRow; onDone: () => void }) {
  const { scope, toast } = useConsole();
  const update = useUpdateEnumLabel();
  const [label, setLabel] = useState(row.label);
  const [shortLabel, setShortLabel] = useState(row.short_label ?? '');
  const [description, setDescription] = useState(row.description ?? '');
  const [order, setOrder] = useState(String(row.sort_order));

  const orderValid = /^\d{1,5}$/.test(order);
  const save = () =>
    update.mutate(
      {
        id: row.id,
        patch: {
          label: label.trim(),
          short_label: shortLabel.trim() || null,
          description: description.trim() || null,
          sort_order: Number(order),
        },
      },
      {
        onSuccess: () => {
          toast({ tone: 'success', message: `${row.enum_value} · ${row.locale} saved` });
          onDone();
        },
      },
    );
  const toggle = () =>
    update.mutate(
      { id: row.id, patch: { is_active: !row.is_active } },
      {
        onSuccess: () => {
          toast({
            tone: row.is_active ? 'info' : 'success',
            message: `${row.enum_value} · ${row.locale} ${row.is_active ? 'switched off' : 'switched on'}`,
          });
          onDone();
        },
      },
    );

  return (
    <Card variant="flat" padding="var(--sp-lg)">
      <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
        <strong>
          {row.enum_name} · {row.enum_value} · {row.locale} · {row.audience}
        </strong>
        <Input density="admin" label="Label" value={label} onChange={setLabel} />
        <Input
          density="admin"
          label="Short label"
          value={shortLabel}
          onChange={setShortLabel}
          helper="For chips where space is tight. Empty uses the label."
        />
        <Input
          density="admin"
          label="Explainer"
          value={description}
          onChange={setDescription}
          multiline
          helper="Shown under the field where the app has room — say what it is used for, never what it brings about."
        />
        <Input
          density="admin"
          label="Order"
          value={order}
          onChange={setOrder}
          inputMode="numeric"
          error={orderValid ? undefined : 'A whole number, 0 to 65535.'}
        />
        {update.isError && <Consequences tone="error" kicker={REFUSED} items={[errorMessage(update.error)]} />}
        <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            disabled={!label.trim() || !orderValid || update.isPending}
            onClick={save}
          >
            Save
          </ScopeAction>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            variant={row.is_active ? 'destructive' : 'secondary'}
            disabled={update.isPending}
            onClick={toggle}
          >
            {row.is_active ? 'Switch off' : 'Switch on'}
          </ScopeAction>
          <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" onClick={onDone}>
            Close
          </ScopeAction>
        </div>
      </div>
    </Card>
  );
}

function NewLabel({
  name,
  suggested,
  rows,
  onDone,
}: {
  name: string;
  suggested: string;
  rows: readonly EnumLabelRow[];
  onDone: () => void;
}) {
  const { scope, toast } = useConsole();
  const create = useCreateEnumLabel();
  const [value, setValue] = useState(suggested);
  const [locale, setLocale] = useState<string>('hi');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');

  /* A translation sorts where its English sibling does; a brand-new code goes last. */
  const sibling = rows.find((r) => r.enum_value === value.trim());
  const sortOrder = sibling ? sibling.sort_order : Math.max(0, ...rows.map((r) => r.sort_order)) + 10;
  const submit = () =>
    create.mutate(
      {
        enum_name: name,
        enum_value: value.trim(),
        locale,
        audience: 'all',
        label: label.trim(),
        description: description.trim() || null,
        sort_order: sortOrder,
      },
      {
        onSuccess: () => {
          toast({ tone: 'success', message: `${value.trim()} · ${locale} added` });
          onDone();
        },
      },
    );

  return (
    <Card variant="flat" padding="var(--sp-lg)">
      <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
        <strong>New label in {name}</strong>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 120px', gap: 'var(--sp-md)' }}>
          <Input density="admin" label="Code" value={value} onChange={setValue} />
          <Select density="admin" label="Locale" value={locale} onChange={setLocale} options={LOCALES} />
        </div>
        <Input density="admin" label="Label" value={label} onChange={setLabel} />
        <Input density="admin" label="Explainer" value={description} onChange={setDescription} multiline />
        {create.isError && <Consequences tone="error" kicker={REFUSED} items={[errorMessage(create.error)]} />}
        <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            disabled={!value.trim() || !label.trim() || create.isPending}
            onClick={submit}
          >
            Add label
          </ScopeAction>
          <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" onClick={onDone}>
            Cancel
          </ScopeAction>
        </div>
      </div>
    </Card>
  );
}

export function EnumLabels() {
  const { scope } = useConsole();
  const [picked, setPicked] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const names = useEnumLabels('');
  const name = picked || names.data?.names[0] || '';
  const list = useEnumLabels(name);
  const rows = name ? (list.data?.items ?? []) : [];
  const gaps = missingHindi(rows);
  const open = rows.find((r) => r.id === editing) ?? null;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-sm)',
          background: 'var(--c-sunk)',
          border: '1px solid var(--c-border)',
          borderRadius: 'var(--r-tile)',
          padding: 'var(--sp-md) var(--sp-lg)',
          fontSize: 'var(--fs-admin-ui)',
          color: 'var(--c-ink2)',
          flexWrap: 'wrap',
        }}
      >
        <Icon name="globe" size={16} />
        <span style={{ flex: 1, minWidth: 280 }}>
          The words the apps and the console show each code as. Edits reach the apps within five
          minutes; every one is checked for outcome claims and recorded in the audit log.
        </span>
        {names.isSuccess && names.data.names.length > 0 && (
          <Select
            density="admin"
            ariaLabel="Code list"
            value={name}
            onChange={(v) => {
              setPicked(v);
              setEditing(null);
              setAdding(false);
            }}
            options={names.data.names}
            style={{ minWidth: 220 }}
          />
        )}
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          icon="plus"
          disabled={!name}
          onClick={() => {
            setEditing(null);
            setAdding(true);
          }}
        >
          New label
        </ScopeAction>
      </div>

      {(names.isPending || (name && list.isPending)) && <LoadingState message="Reading the labels…" />}
      {names.isError && <ErrorState title="Could not read the labels" cause={errorMessage(names.error)} />}
      {list.isError && <ErrorState title="Could not read the labels" cause={errorMessage(list.error)} />}

      {names.isSuccess && names.data.names.length === 0 && (
        <Card variant="flat" padding="var(--sp-xxl)">
          <EmptyState
            icon="globe"
            title="No labels yet"
            body="Labels arrive with the migration that adds the code reading them. An empty table means no migration has run, not that labels are switched off."
          />
        </Card>
      )}

      {gaps.length > 0 && (
        <Card variant="flat" padding="var(--sp-md) var(--sp-lg)">
          <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
            <strong>No Hindi label for {gaps.length}:</strong> {gaps.join(', ')}. The app shows the
            English one there until it is added.
          </span>
        </Card>
      )}

      {adding && (
        <NewLabel key={name} name={name} rows={rows} suggested={gaps[0] ?? ''} onDone={() => setAdding(false)} />
      )}
      {open && <Editor key={open.id} row={open} onDone={() => setEditing(null)} />}

      {list.isSuccess && rows.length > 0 && (
        <DataTable
          density="comfortable"
          onRowClick={(row) => {
            setAdding(false);
            setEditing(Number(row.id));
          }}
          footer={<span>{rows.length} labels</span>}
          columns={[
            { key: 'value', label: 'Code', mono: true },
            { key: 'locale', label: 'Locale' },
            { key: 'audience', label: 'For' },
            { key: 'label', label: 'Label' },
            { key: 'explainer', label: 'Explainer' },
            { key: 'order', label: 'Order', align: 'right' },
            { key: 'status', label: 'Status' },
          ]}
          rows={rows.map((r) => ({
            id: r.id,
            value: r.enum_value,
            locale: r.locale,
            audience: r.audience,
            label: r.label,
            explainer: r.description || <span style={{ color: 'var(--c-ink3)' }}>—</span>,
            order: r.sort_order,
            status: <StatusChip tone={r.is_active ? 'success' : 'neutral'} label={r.is_active ? 'Shown' : 'Off'} />,
          }))}
        />
      )}
    </>
  );
}
